import { useEffect, useState } from "react";
import { Copy, Mail, Share2, X, Check, Trophy } from "lucide-react";
import { toast } from "sonner";
import {
  buildReferralUrl,
  getOrCreateReferralCode,
  getReferralStats,
  shareTargets,
  TIER_LABELS,
  TIER_THRESHOLDS,
  type RewardTier,
} from "@/lib/referrals";
import { trackEvent } from "@/lib/audit";
import { awardPoints } from "@/lib/gamification";
import { bumpShareSuccess } from "@/lib/viral/shareCounter";
import { useLanguage } from "@/hooks/useLanguage";
import { withShareUtm } from "@/lib/share/nationWarsShare";
import { useSelectedNation } from "@/hooks/useSelectedNation";

export function ShareSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useLanguage();
  const MSG = t("share_msg");
  const [code, setCode] = useState<string>("");
  const [url, setUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [feedback, setFeedback] = useState(false);
  const [count, setCount] = useState(0);
  const [unlocked, setUnlocked] = useState<RewardTier[]>([]);
  const [next, setNext] = useState<{ tier: RewardTier; remaining: number } | null>(null);
  // P0-SHARE-02 — identity nation MUST come from profiles.selection_team,
  // never from geo IP. Otherwise users in the US share /s/US for every nation.
  const { nation } = useSelectedNation();

  useEffect(() => {
    if (!open) return;
    void trackEvent("share_open", {});
    void trackEvent("share_tier_viewed", {});
    void awardPoints("share_open").then((amount) => {
      if (amount > 0) {
        toast.success(`+${amount} pts`, {
          description: "Gracias por compartir VerseSport",
        });
      }
    });
    (async () => {
      const c = await getOrCreateReferralCode();
      setCode(c);
      setUrl(buildReferralUrl(c));
      const s = await getReferralStats();
      setCount(s.count);
      setUnlocked(s.unlocked);
      setNext(s.next);
    })();
  }, [open]);

  if (!open) return null;

  const taggedUrl = url
    ? withShareUtm(url, { source: "share_sheet", nation, campaign: "referral" })
    : url;
  const targets = shareTargets(taggedUrl, MSG);
  const tiers = Object.keys(TIER_THRESHOLDS) as RewardTier[];
  // P0-VIRAL-01 — explicit reward microcopy per tier. Visual-only;
  // does NOT change scoring or referral thresholds.
  const REWARD_POINTS: Record<RewardTier, number> = {
    analista_pro: 50,
    fan_oro: 250,
    leyenda: 500,
  };
  const progress = next
    ? Math.max(0, Math.min(100, ((TIER_THRESHOLDS[next.tier] - next.remaining) / TIER_THRESHOLDS[next.tier]) * 100))
    : 100;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${MSG} ${taggedUrl || url}`);
      setCopied(true);
      toast.success(t("share_copied"));
      setTimeout(() => setCopied(false), 1800);
      onShareSuccess();
    } catch {
      toast.error(t("share_copy_failed"));
    }
  };

  const onShareSuccess = () => {
    bumpShareSuccess();
    setFeedback(true);
    void trackEvent("share_success_feedback_shown", {});
    window.setTimeout(() => setFeedback(false), 3000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-3 sm:items-center" onClick={onClose}>
      <div
        className="w-full max-w-md overflow-hidden rounded-2xl border border-border/60 glass shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border/60 px-4 py-3">
          <div className="flex items-center gap-2">
            <Share2 className="h-4 w-4 text-primary" />
            <div className="text-sm font-bold">{t("share_title")}</div>
          </div>
          <button onClick={onClose} aria-label={t("common_close")} className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 px-4 py-4">
          {/* progress */}
          <div className="rounded-xl bg-accent/30 p-3">
            <div className="flex items-center justify-between text-[11px] font-bold">
              <span className="flex items-center gap-1.5">
                <Trophy className="h-3.5 w-3.5 text-[oklch(0.85_0.18_85)]" />
                {t("share_referrals")}
              </span>
              <span className="tabular-nums">{count}</span>
            </div>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-secondary">
              <div
                className="h-full bg-gradient-to-r from-[oklch(0.58_0.24_27)] to-[oklch(0.82_0.24_145)] transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {tiers.map((t) => {
                const u = unlocked.includes(t);
                return (
                  <div
                    key={t}
                    className={`rounded-lg p-2 text-center text-[10px] font-bold ${
                      u ? "bg-primary/20 text-primary" : "bg-secondary/40 text-muted-foreground"
                    }`}
                  >
                    {u ? "✦ " : ""}
                    {TIER_LABELS[t]}
                    <div className="text-[9px] opacity-70">{TIER_THRESHOLDS[t]}+</div>
                    <div className="mt-0.5 text-[10px] font-black text-foreground">
                      +{REWARD_POINTS[t]} pts
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="mt-2 text-[10px] leading-snug text-muted-foreground">
              Cada amigo que se registra con tu enlace suma puntos para tu nación.
            </p>
            {next && (
              <div className="mt-2 text-[10px] text-muted-foreground">
                {t("share_remaining", { n: next.remaining, tier: TIER_LABELS[next.tier] })}
              </div>
            )}
          </div>

          {/* link */}
          <div>
            <div className="text-[10px] font-bold tracking-widest text-muted-foreground">{t("share_link_label")}</div>
            <div className="mt-1 flex items-center gap-2 rounded-lg bg-accent/40 px-3 py-2">
              <code className="flex-1 truncate text-[11px]">{url || "…"}</code>
              <button
                onClick={onCopy}
                className="flex h-7 w-7 items-center justify-center rounded-md bg-primary text-primary-foreground"
                aria-label={t("common_copy")}
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              </button>
            </div>
            {code && <div className="mt-1 text-[10px] text-muted-foreground">{t("share_code_label")}: {code}</div>}
          </div>

          {/* targets */}
          <div className="grid grid-cols-4 gap-2">
            <a href={targets.whatsapp} target="_blank" rel="noreferrer" onClick={onShareSuccess} className="ShareBtn">
              <span className="text-lg">💬</span>
              <span>WhatsApp</span>
            </a>
            <a href={targets.x} target="_blank" rel="noreferrer" onClick={onShareSuccess} className="ShareBtn">
              <span className="text-lg">𝕏</span>
              <span>X</span>
            </a>
            <a href={targets.facebook} target="_blank" rel="noreferrer" onClick={onShareSuccess} className="ShareBtn">
              <span className="text-lg">f</span>
              <span>Facebook</span>
            </a>
            <a href={targets.email} onClick={onShareSuccess} className="ShareBtn">
              <Mail className="h-4 w-4" />
              <span>Email</span>
            </a>
          </div>
          {feedback && (
            <div className="rounded-lg border border-[oklch(0.82_0.24_145)]/50 bg-[oklch(0.82_0.24_145)]/15 px-3 py-2 text-[11px] font-black text-foreground">
              ✅ Compartido correctamente · +50 puntos para tu nación
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
